import React, { useState, useEffect } from 'react';
import { useFeedback } from '../../contexts/FeedbackContext';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import campaignService from '../../services/campaignService';
import LoadingSpinner from '../../components/LoadingSpinner';
import Breadcrumb from '../../components/common/Breadcrumb';
import CSRFToken from '../../security/CSRFToken';
import { sanitizeText, sanitizeUrl, sanitizeObject, sanitizeContentGuidelines } from '../../security/sanitize';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';
import { DatePicker } from '../../components/ui/date-picker';

const CampaignEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showSuccess, showError } = useFeedback();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [campaignImage, setCampaignImage] = useState(null);
  const [campaignImagePreview, setCampaignImagePreview] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    applicationDeadline: '',
    reviewDeadline: '',
    endDate: '',
    budget: '',
    compensationType: 'monetary',
    compensationAmount: '',
    productDetails: '',
    category: '',
    imageUrl: '',
    contentType: [],
    contentGuidelines: '',
    requiredHashtags: '',
    requiredMentions: '',
    minFollowers: 0,
    preferredNiches: []
  });

  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch real campaign data from Firestore
    const fetchCampaignData = async () => {
      if (!id) return;
      
      try {
        setLoading(true);

        
        // Get campaign from Firestore
        const campaignData = await campaignService.getCampaignById(id);

        
        if (campaignData) {
          // Verify this campaign belongs to the current brand user
          if (campaignData.brandId === currentUser?.uid) {
            setCampaign(campaignData);
            
            // Format dates for form inputs if they are Date objects
            const formatDateForInput = (date) => {
              if (!date) return '';
              if (typeof date === 'string') return date;
              if (date.toDate) return date.toDate().toISOString().split('T')[0];
              if (date instanceof Date) {
                return date.toISOString().split('T')[0];
              }
              return '';
            };
            
            setFormData({
              name: campaignData.name || '',
              description: campaignData.description || '',
              startDate: formatDateForInput(campaignData.startDate),
              applicationDeadline: formatDateForInput(campaignData.applicationDeadline || campaignData.endDate),
              reviewDeadline: formatDateForInput(campaignData.reviewDeadline),
              endDate: formatDateForInput(campaignData.endDate),
              budget: campaignData.budget || '',
              compensationType: campaignData.compensationType || 'monetary',
              compensationAmount: campaignData.compensationAmount || '',
              productDetails: campaignData.productDetails || '',
              category: campaignData.category || '',
              imageUrl: campaignData.imageUrl || '',
              contentType: campaignData.contentType || [],
              contentGuidelines: campaignData.contentGuidelines || '',
              requiredHashtags: campaignData.requiredHashtags || '',
              requiredMentions: campaignData.requiredMentions || '',
              minFollowers: campaignData.minFollowers || 0,
              preferredNiches: campaignData.preferredNiches || []
            });
            
            // Set existing image preview if available
            if (campaignData.imageUrl) {
              setCampaignImagePreview(campaignData.imageUrl);
            }
          } else {
            console.error('Unauthorized access to campaign');
            setCampaign(null);
          }
        } else {
          console.error('Campaign not found');
          setCampaign(null);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching campaign data:', error);
        setError('Failed to load campaign. Please check your connection and try again.');
        setLoading(false);
      }
    };

    fetchCampaignData();
  }, [id, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Image upload handler
  const handleCampaignImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Invalid file.', 'Please select a valid image file.', 4000);
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('File too large.', 'Image size must be less than 5MB.', 4000);
        return;
      }
      
      setCampaignImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setCampaignImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get CSRF token from form
    const form = new FormData(e.target);
    const csrfToken = form.get('csrfToken');
    
    // Validate CSRF token
    if (!csrfToken || csrfToken !== sessionStorage.getItem('csrfToken')) {
      console.error('CSRF token validation failed');
      showError('Security validation failed.', 'Please refresh the page and try again.', 6000);
      return;
    }

    // Date validation
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      showError('Invalid date range.', 'Campaign start date must be before the end date.', 5000);
      return;
    }
    if (formData.applicationDeadline && formData.endDate && formData.applicationDeadline > formData.endDate) {
      showError('Invalid deadline.', 'Application deadline must be before the campaign end date.', 5000);
      return;
    }
    
    setSaving(true);
    
    try {
      // Upload new campaign image if provided
      let imageUrl = formData.imageUrl;
      if (campaignImage) {
        setImageUploading(true);
        try {
          const imageRef = ref(storage, `campaignImages/${currentUser.uid}/${Date.now()}_${campaignImage.name}`);
          const uploadResult = await uploadBytes(imageRef, campaignImage);
          imageUrl = await getDownloadURL(uploadResult.ref);
        } finally {
          setImageUploading(false);
        }
      }

      // Sanitize all form data
      const updatedCampaignData = {
        name: sanitizeText(formData.name),
        description: sanitizeText(formData.description),
        startDate: formData.startDate,
        applicationDeadline: formData.applicationDeadline,
        reviewDeadline: formData.reviewDeadline,
        endDate: formData.endDate,
        budget: sanitizeText(formData.budget),
        compensationType: sanitizeText(formData.compensationType),
        compensationAmount: sanitizeText(formData.compensationAmount),
        productDetails: sanitizeText(formData.productDetails),
        category: sanitizeText(formData.category),
        imageUrl: imageUrl,
        contentType: formData.contentType.map(type => sanitizeText(type)),
        contentGuidelines: sanitizeContentGuidelines(formData.contentGuidelines),
        requiredHashtags: sanitizeText(formData.requiredHashtags),
        requiredMentions: sanitizeText(formData.requiredMentions),
        minFollowers: formData.minFollowers,
        preferredNiches: formData.preferredNiches.map(niche => sanitizeText(niche))
      };
      
      const updateData = {
        ...updatedCampaignData,
        updatedAt: new Date()
      };
            
      await campaignService.updateCampaign(id, updateData);
      
      setSaving(false);
      showSuccess('Campaign updated successfully.', 'Your changes have been saved.', 3000);
      navigate(`/brand/campaigns/${id}`);
    } catch (error) {
      console.error('Error updating campaign:', error);
      setSaving(false);
      showError('Failed to save campaign.', 'Please check your connection and try again.', 6000);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" color="primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Something went wrong</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="max-w-4xl mx-auto">

        <div className="bg-white rounded-lg shadow p-8 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Campaign Not Found</h3>
          <p className="text-gray-600 mb-4">The campaign you're looking for doesn't exist or has been removed.</p>
          <Link
            to="/brand/campaigns"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            Back to Campaigns
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Toast Notifications */}

      {/* Breadcrumb */}
      <Breadcrumb
        items={[
          { label: 'Campaigns', to: '/brand/campaigns' },
          { label: campaign?.name || 'Campaign', to: `/brand/campaigns/${id}` },
          { label: 'Edit' },
        ]}
      />

      {/* Header */}
      <div className="mb-8 bg-white rounded-lg shadow-sm p-5 border border-gray-100">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Edit Campaign</h2>
          <p className="text-sm text-gray-500 mt-1">Make changes to your campaign details below</p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md border border-gray-100">
        <form onSubmit={handleSubmit} className="p-8">
          <CSRFToken />
          {/* Basic Information */}
          <div className="mb-10">
            <div className="mb-6 pb-2 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800">Basic Information</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-lg">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Name
                </label>
                <input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => handleChange({ target: { name: 'category', value } })}
                >
                  <SelectTrigger id="category" className="w-full">
                    <SelectValue placeholder="Select a category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fashion">Fashion</SelectItem>
                    <SelectItem value="Beauty">Beauty</SelectItem>
                    <SelectItem value="Food">Food</SelectItem>
                    <SelectItem value="Travel">Travel</SelectItem>
                    <SelectItem value="Technology">Technology</SelectItem>
                    <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                    <SelectItem value="Health & Fitness">Health & Fitness</SelectItem>
                    <SelectItem value="Home & Decor">Home & Decor</SelectItem>
                    <SelectItem value="Entertainment">Entertainment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  id="description"
                  name="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                  required
                ></textarea>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Campaign Image</label>
                <p className="text-xs text-gray-500 mb-3">Recommended size: 800x400 pixels (16:9 ratio). Maximum file size: 5MB</p>
                
                {campaignImagePreview ? (
                  <div className="mb-4">
                    <div className="relative inline-block">
                      <img
                        src={campaignImagePreview}
                        alt="Campaign preview"
                        className="w-full max-w-md h-48 object-cover rounded-lg border border-gray-300"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCampaignImage(null);
                          setCampaignImagePreview(null);
                          setFormData(prev => ({ ...prev, imageUrl: '' }));
                        }}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-md px-2 py-1 shadow-lg transition duration-200"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="mb-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition duration-200">
                      <div className="mt-4">
                        <label htmlFor="campaignImageEdit" className="cursor-pointer">
                          <span className="mt-2 block text-sm font-medium text-gray-900">
                            Upload campaign image
                          </span>
                          <span className="mt-1 block text-xs text-gray-500">
                            PNG, JPG, GIF up to 5MB
                          </span>
                        </label>
                        <input
                          id="campaignImageEdit"
                          name="campaignImageEdit"
                          type="file"
                          accept="image/*"
                          onChange={handleCampaignImageChange}
                          className="sr-only"
                        />
                      </div>
                    </div>
                  </div>
                )}
                
                {!campaignImagePreview && (
                  <button
                    type="button"
                    onClick={() => document.getElementById('campaignImageEdit').click()}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    Choose Image
                  </button>
                )}
                
                {imageUploading && (
                  <div className="mt-2 flex items-center text-sm text-gray-600">
                    <LoadingSpinner size="sm" />
                    <span className="ml-2">Uploading image...</span>
                  </div>
                )}
              </div>
              
              <div>
                <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign Start
                </label>
                <DatePicker
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={(value) => handleChange({ target: { name: 'startDate', value } })}
                  placeholder="Pick a date"
                />
              </div>
              
              <div>
                <label htmlFor="applicationDeadline" className="block text-sm font-medium text-gray-700 mb-1">
                  Application Deadline
                </label>
                <DatePicker
                  id="applicationDeadline"
                  name="applicationDeadline"
                  value={formData.applicationDeadline}
                  onChange={(value) => handleChange({ target: { name: 'applicationDeadline', value } })}
                  placeholder="Pick a date"
                />
              </div>

              <div>
                <label htmlFor="reviewDeadline" className="block text-sm font-medium text-gray-700 mb-1">
                  Review Deadline
                </label>
                <DatePicker
                  id="reviewDeadline"
                  name="reviewDeadline"
                  value={formData.reviewDeadline}
                  onChange={(value) => handleChange({ target: { name: 'reviewDeadline', value } })}
                  placeholder="Pick a date"
                />
              </div>
              
              <div>
                <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign End
                </label>
                <DatePicker
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={(value) => handleChange({ target: { name: 'endDate', value } })}
                  placeholder="Pick a date"
                />
              </div>
            </div>
          </div>
          
          {/* Compensation */}
          <div className="mb-10">
            <div className="mb-6 pb-2 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800">Compensation</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-lg">
              <div>
                <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                  Total Budget
                </label>
                <input
                  type="text"
                  name="budget"
                  id="budget"
                  value={formData.budget}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                  required
                />
              </div>
              
              <div>
                <label htmlFor="compensationType" className="block text-sm font-medium text-gray-700 mb-1">
                  Compensation Type
                </label>
                <Select
                  value={formData.compensationType}
                  onValueChange={(value) => handleChange({ target: { name: 'compensationType', value } })}
                >
                  <SelectTrigger id="compensationType" className="w-full">
                    <SelectValue placeholder="Select compensation type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monetary">Monetary</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="both">Both (Monetary + Product)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="compensationAmount" className="block text-sm font-medium text-gray-700 mb-1">
                  Compensation Details (PHP)*
                </label>
                <input
                  type="text"
                  name="compensationAmount"
                  id="compensationAmount"
                  value={formData.compensationAmount}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, '');
                    const formattedValue = value ? parseFloat(value).toLocaleString('en-US', {minimumFractionDigits: 0, maximumFractionDigits: 2}) : '';
                    handleChange({target: {name: 'compensationAmount', value: formattedValue}});
                  }}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50 py-2 px-3"
                  placeholder="5,000"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Amount per influencer (e.g. per post, per review)</p>
              </div>
              
              {(formData.compensationType === 'product' || formData.compensationType === 'both') && (
                <div className="md:col-span-2">
                  <label htmlFor="productDetails" className="block text-sm font-medium text-gray-700 mb-1">
                    Product Details*
                  </label>
                  <textarea
                    name="productDetails"
                    id="productDetails"
                    rows={3}
                    value={formData.productDetails}
                    onChange={handleChange}
                    className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                    placeholder="Describe the product(s) you'll be providing to influencers..."
                    required
                  />
                </div>
              )}
            </div>
          </div>
          
          {/* Content Requirements */}
          <div className="mb-10">
            <div className="mb-6 pb-2 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-800">Content Requirements</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-6 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Content Type
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-3 border border-gray-300 rounded-md bg-white">
                  {[
                    "Instagram Post",
                    "Instagram Story",
                    "Instagram Reel",
                    "TikTok Video",
                    "YouTube Video",
                    "Blog Post",
                    "Facebook Post",
                    "Twitter Post"
                  ].map((type) => (
                    <div key={type} className="flex items-center">
                      <input
                        id={`content-${type}`}
                        name="contentType"
                        type="checkbox"
                        value={type}
                        checked={formData.contentType.includes(type)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const isChecked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            contentType: isChecked
                              ? [...prev.contentType, value]
                              : prev.contentType.filter(item => item !== value)
                          }));
                        }}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor={`content-${type}`} className="ml-2 block text-sm text-gray-700">
                        {type}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">Select all that apply</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Preferred Niches
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto p-3 border border-gray-300 rounded-md bg-white">
                  {[
                    "Fashion",
                    "Beauty",
                    "Food",
                    "Travel",
                    "Technology",
                    "Lifestyle",
                    "Health & Fitness",
                    "Home & Decor",
                    "Entertainment"
                  ].map((niche) => (
                    <div key={niche} className="flex items-center">
                      <input
                        id={`niche-${niche}`}
                        name="preferredNiches"
                        type="checkbox"
                        value={niche}
                        checked={formData.preferredNiches.includes(niche)}
                        onChange={(e) => {
                          const value = e.target.value;
                          const isChecked = e.target.checked;
                          setFormData(prev => ({
                            ...prev,
                            preferredNiches: isChecked
                              ? [...prev.preferredNiches, value]
                              : prev.preferredNiches.filter(item => item !== value)
                          }));
                        }}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                      />
                      <label htmlFor={`niche-${niche}`} className="ml-2 block text-sm text-gray-700">
                        {niche}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">Select all that apply</p>
              </div>
              
              <div className="md:col-span-2">
                <label htmlFor="contentGuidelines" className="block text-sm font-medium text-gray-700 mb-1">
                  Content Guidelines
                </label>
                <textarea
                  id="contentGuidelines"
                  name="contentGuidelines"
                  rows="4"
                  value={formData.contentGuidelines}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                  required
                ></textarea>
              </div>
              
              <div>
                <label htmlFor="requiredHashtags" className="block text-sm font-medium text-gray-700 mb-1">
                  Required Hashtags
                </label>
                <input
                  type="text"
                  name="requiredHashtags"
                  id="requiredHashtags"
                  value={formData.requiredHashtags}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                  placeholder="e.g. #BrandName #CampaignName"
                />
              </div>
              
              <div>
                <label htmlFor="requiredMentions" className="block text-sm font-medium text-gray-700 mb-1">
                  Required Mentions
                </label>
                <input
                  type="text"
                  name="requiredMentions"
                  id="requiredMentions"
                  value={formData.requiredMentions}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                  placeholder="e.g. @brandname"
                />
              </div>
              
              <div>
                <label htmlFor="minFollowers" className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Followers
                </label>
                <input
                  type="number"
                  name="minFollowers"
                  id="minFollowers"
                  value={formData.minFollowers}
                  onChange={handleChange}
                  className="shadow-sm focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md transition-all duration-200 hover:border-primary/50"
                  min="0"
                />
              </div>
            </div>
          </div>
          
          {/* Submit Button */}
          <div className="mt-10 pt-6 border-t border-gray-200">
            <div className="flex justify-end space-x-4">
              <Link
                to={`/brand/campaigns/${id}`}
                className="inline-flex items-center justify-center py-2.5 px-6 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Cancel
              </Link>
              <button
                type="submit"
                className="inline-flex items-center justify-center py-2.5 px-6 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <LoadingSpinner size="xs" color="white" />
                    <span className="ml-2">Saving...</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CampaignEdit;
